import type { TsukumogamiRarity } from '../utils/collection';

/**
 * P9-1「欠けの可視化」: 図鑑の収集対象の全体集合（マニフェスト）。
 *
 * - MVP は Player 内静的定義。将来は Worker 配信へ昇格する（提案書 §8.2-1 / P9-2 §8.3）。
 * - **公開済シナリオ分のみ**載せる（§10 論点④確定: 未公開シナリオのネタバレ制御）。
 * - `key` は utils/collection.ts の resolveKey と同一規約で**実際に記録されるキー**に一致させること。
 *   EndingView → recordTsukumogami は scenarioId を渡さない（EndingView は scenarioId を
 *   props に持たない）ため、公開 Player の実キーは `name:<ending.tsukumogamiData.name>` になる。
 *   不一致だと「収集済みなのに？？？のまま」になる。シナリオ追加時は該当 ending step の
 *   tsukumogamiData.name と突き合わせて 1 エントリ追記する。
 */
export interface ZukanManifestEntry {
  /** collection.ts の resolveKey 規約と一致する一意キー（例 `name:オクリ（旧潮待駅の発車時刻表）`）。 */
  key: string;
  /** この枠が属するシナリオ。単体デモでは現在シナリオの枠だけを見せるために使う。 */
  scenarioIds?: string[];
  /** レアリティ（未収集スロットにも格の示唆として出す。名前・説明は出さない）。 */
  rarity?: TsukumogamiRarity;
  /** 収集済みカードに表示する最新版の説明。過去の端末保存より優先する。 */
  description?: string;
  /** 未収集時に見せる一文ヒント（ネタバレしない範囲の誘い文句）。 */
  hint: string;
  /**
   * シナリオが宣言する結末の総数（ending step の実数）。分かる場合のみ宣言する（捏造しない）。
   * 宣言があれば UI は「n / m」、無ければ n のみを表示する。
   */
  declaredEndingCount?: number;
}

/**
 * 営業デモ分の図鑑枠の吸収（literal glob・詳細は ../scenarios/demo.ts ヘッダ）。
 * `vite build` では exclude-demo-scenarios plugin が空スタブ（demoZukanEntries=[]）へ
 * 差し替えるため、フラグなしの本番図鑑は従来と 1 エントリも変わらない。
 * `VITE_INCLUDE_DEMO=1` build と dev のときだけデモ枠が並ぶ。
 */
const demoModules = import.meta.glob<{ demoZukanEntries?: ZukanManifestEntry[] }>(
  '../scenarios/demo.ts',
  { eager: true }
);
const demoZukanEntries: ZukanManifestEntry[] =
  demoModules['../scenarios/demo.ts']?.demoZukanEntries ?? [];

export const ZUKAN_MANIFEST: ZukanManifestEntry[] = [...demoZukanEntries];

/** key → manifest エントリの索引（収集済みカードに declaredEndingCount を引くのに使う）。 */
export function getZukanManifestByKey(): Map<string, ZukanManifestEntry> {
  return new Map(ZUKAN_MANIFEST.map((entry) => [entry.key, entry]));
}

/**
 * 図鑑はシナリオをまたぐ共通台帳として表示する。
 * scenarioId は既存呼び出しとの互換用に受け取るが、表示の絞り込みには使わない。
 */
export function getZukanManifestForScenario(_scenarioId?: string): ZukanManifestEntry[] {
  return ZUKAN_MANIFEST;
}
